import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, XCircle, ArrowRight, Zap, Shield, Clock, 
  FileText, Bell, BarChart3, Calculator, CreditCard, Sparkles, Globe
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const SmartFactureVsTiime = () => {
  const comparisons = [
    { feature: "Format Factur-X conforme 2026", smartfacture: true, competitor: true },
    { feature: "Relances automatiques impayés", smartfacture: true, competitor: true },
    { feature: "Scanner IA de factures", smartfacture: true, competitor: false },
    { feature: "Module URSSAF intégré", smartfacture: true, competitor: false },
    { feature: "Rapprochement bancaire", smartfacture: true, competitor: true },
    { feature: "Export comptable CSV", smartfacture: true, competitor: true },
    { feature: "Dashboard temps réel", smartfacture: true, competitor: true },
    { feature: "Compte bancaire intégré", smartfacture: false, competitor: true },
    { feature: "Essai gratuit sans CB", smartfacture: true, competitor: false },
    { feature: "À partir de 14€/mois", smartfacture: true, competitor: false },
  ];

  const smartfactureAdvantages = [
    {
      icon: Sparkles,
      title: "Scanner IA avancé",
      description: "Notre IA extrait automatiquement toutes les données de vos factures fournisseurs en quelques secondes. Tiime n'offre pas cette fonctionnalité."
    },
    {
      icon: Calculator,
      title: "Module URSSAF unique",
      description: "Simulez vos cotisations sociales et anticipez vos charges trimestrielles. Un outil exclusif à SmartFacture AI."
    },
    {
      icon: CreditCard,
      title: "Prix sans compte bancaire imposé",
      description: "Contrairement à Tiime, vous n'êtes pas obligé d'ouvrir un compte bancaire. Choisissez librement votre banque."
    },
    {
      icon: Clock,
      title: "Essai gratuit complet",
      description: "7 jours d'essai gratuit sans carte bancaire. Tiime nécessite souvent d'ouvrir un compte pour tester."
    }
  ];

  const faqs = [
    {
      q: "SmartFacture AI ou Tiime : lequel choisir ?",
      a: "Si vous voulez une solution de facturation pure sans être lié à une banque, SmartFacture AI est le meilleur choix. Tiime est intéressant si vous cherchez une solution bancaire + comptabilité tout-en-un."
    },
    {
      q: "Tiime oblige-t-il à ouvrir un compte bancaire ?",
      a: "Oui, le modèle de Tiime repose sur l'ouverture d'un compte bancaire professionnel. SmartFacture AI vous laisse libre de choisir votre banque."
    },
    {
      q: "SmartFacture AI est-il conforme comme Tiime ?",
      a: "Oui, SmartFacture AI génère des factures Factur-X conformes à la réforme 2026, tout comme Tiime."
    },
    {
      q: "Quel est le prix de SmartFacture vs Tiime ?",
      a: "SmartFacture démarre à 14€/mois. Tiime est souvent \"gratuit\" mais se rémunère sur les frais bancaires et les services additionnels."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>SmartFacture AI vs Tiime : Comparatif 2026 | Sans compte bancaire imposé</title>
        <meta name="description" content="Comparatif SmartFacture AI vs Tiime. Découvrez les différences : scanner IA, module URSSAF, liberté bancaire. Faites le meilleur choix pour votre entreprise." />
        <link rel="canonical" href="https://smartfacture-ai.com/comparaison/smartfacture-vs-tiime" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Essai gratuit
              </Button>
            </Link>
          </div>
        </header>

        {/* Hero */}
        <section className="bg-gradient-to-br from-emerald-600 to-teal-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              Comparatif Indépendance
            </Badge>
            <h1 className="text-3xl lg:text-5xl font-bold mb-6">
              SmartFacture AI vs Tiime
            </h1>
            <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
              Vous voulez une solution de facturation sans être lié à une banque ? Découvrez pourquoi SmartFacture AI est l'alternative idéale à Tiime.
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-12">
          {/* Key Difference Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-12 text-center">
            <Globe className="h-8 w-8 text-amber-600 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              La différence clé
            </h3>
            <p className="text-slate-700">
              <strong>Tiime</strong> impose l'ouverture d'un compte bancaire chez eux. <br />
              <strong>SmartFacture AI</strong> vous laisse libre de choisir votre banque.
            </p>
          </div>

          {/* Comparison Table */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Tableau comparatif
            </h2>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-3 bg-slate-100 p-4 font-semibold text-slate-700">
                <div>Fonctionnalité</div>
                <div className="text-center text-blue-600">SmartFacture AI</div>
                <div className="text-center">Tiime</div>
              </div>
              {comparisons.map((item, idx) => (
                <div key={idx} className={`grid grid-cols-3 p-4 items-center ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                  <div className="text-slate-700">{item.feature}</div>
                  <div className="text-center">
                    {item.smartfacture ? (
                      <CheckCircle className="h-6 w-6 text-emerald-500 mx-auto" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-400 mx-auto" />
                    )}
                  </div>
                  <div className="text-center">
                    {item.competitor ? (
                      <CheckCircle className="h-6 w-6 text-emerald-500 mx-auto" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-400 mx-auto" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Advantages */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Pourquoi choisir SmartFacture AI plutôt que Tiime ?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {smartfactureAdvantages.map((adv, idx) => {
                const Icon = adv.icon;
                return (
                  <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{adv.title}</h3>
                    <p className="text-slate-600">{adv.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-emerald-600 to-teal-500 rounded-2xl p-8 lg:p-12 text-white text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Gardez votre liberté bancaire
            </h2>
            <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
              Choisissez SmartFacture AI pour une facturation conforme 2026 sans être obligé de changer de banque.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-emerald-600 hover:bg-emerald-50 font-semibold rounded-xl transition-colors"
              >
                Essai gratuit 7 jours
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link 
                to="/demo"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white text-white hover:bg-white/10 font-semibold rounded-xl transition-colors"
              >
                Voir la démo
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Questions fréquentes
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Other Comparisons */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 text-center mb-6">
              Autres comparatifs
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/comparaison/smartfacture-vs-indy" className="text-blue-600 hover:underline">
                SmartFacture vs Indy
              </Link>
              <Link to="/comparaison/smartfacture-vs-freebe" className="text-blue-600 hover:underline">
                SmartFacture vs Freebe
              </Link>
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-slate-400">© 2026 SmartFacture AI. Tous droits réservés.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SmartFactureVsTiime;
